import Translations from "@translate/components/Member/Subscription/onboardingPricingCopy";
import { I18nUtil } from "@utils/i18n";
import type { OnboardingPricingOffer } from "./onboardingPricingOffers";
import CycleTranslations from "@translate/components/Member/Subscription/SubscriptionCard/SubscriptionCard";
import AnnualUnitTranslations from "@translate/components/OnboardingWizard/result/ResultIdleCashHeroValue";

const I18n = I18nUtil.createScoped(Translations);
const CycleI18n = I18nUtil.createScoped(CycleTranslations);
const AnnualUnitI18n = I18nUtil.createScoped(AnnualUnitTranslations);

export const pricingMoney = (amount: number, locale?: string) =>
  new Intl.NumberFormat(locale || "en", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

export const onboardingPricingCopy = (
  offer: OnboardingPricingOffer,
  locale?: string,
) => {
  const price = pricingMoney(offer.price, locale);
  const title = {
    yearly: I18n.t("Annual", { locale }),
    monthly: I18n.t("Monthly", { locale }),
    weekly: I18n.t("Weekly", { locale }),
    lifetime: I18n.t("Lifetime", { locale }),
  }[offer.key];
  const unit = {
    yearly: AnnualUnitI18n.t("/yr", { locale }),
    monthly: CycleI18n.t("/mo", { locale }),
    weekly: CycleI18n.t("/wk", { locale }),
    lifetime: "",
  }[offer.key];
  const payback = offer.paybackYears
    ? I18n.t("Pays off in %{years} yr", { locale, years: offer.paybackYears })
    : undefined;
  const tag = {
    yearly: offer.discount
      ? I18n.t("Save %{discount}%", { locale, discount: offer.discount })
      : undefined,
    monthly: I18n.t("Renews monthly", { locale }),
    weekly: I18n.t("Try it out", { locale }),
    lifetime: payback,
  }[offer.key];
  const detail =
    offer.dailyPrice != null
      ? I18n.t("%{price} a day", {
          locale,
          price: pricingMoney(offer.dailyPrice, locale),
        })
      : I18n.t("One-time payment", { locale });
  const trial =
    offer.trial && offer.trial.price != null && offer.trial.days != null
      ? { price: offer.trial.price, days: offer.trial.days }
      : undefined;
  const trialNotReady = offer.purchaseProduct.type === "TRIAL" && !trial;
  const cta = trialNotReady
    ? undefined
    : offer.key === "lifetime"
      ? I18n.t("Get Ed Lifetime · %{price}", { locale, price })
      : trial
        ? trial.price === 0
          ? I18n.t("Start my %{days}-day free trial", {
              locale,
              days: trial.days,
            })
          : I18n.t("Start my %{days}-day trial", {
              locale,
              days: trial.days,
            })
        : I18n.t("Get Ed Complete", { locale });
  const disclosure = trialNotReady
    ? undefined
    : trial
      ? trial.price === 0
        ? trial.days === 1
          ? I18n.t("Free for 1 day, then %{price}/yr unless cancelled.", {
              locale,
              price,
            })
          : I18n.t(
              "Free for %{days} days, then %{price}/yr unless cancelled.",
              {
                locale,
                days: trial.days,
                price,
              },
            )
        : trial.days === 1
          ? I18n.t(
              "%{trialPrice} for 1 day, then %{price}/yr unless cancelled.",
              {
                locale,
                trialPrice: pricingMoney(trial.price, locale),
                price,
              },
            )
          : I18n.t(
              "%{trialPrice} for %{days} days, then %{price}/yr unless cancelled.",
              {
                locale,
                trialPrice: pricingMoney(trial.price, locale),
                days: trial.days,
                price,
              },
            )
      : {
          yearly: I18n.t("%{price}/yr, renews yearly unless cancelled.", {
            locale,
            price,
          }),
          monthly: I18n.t("%{price}/mo, renews monthly unless cancelled.", {
            locale,
            price,
          }),
          weekly: I18n.t("%{price}/wk, renews weekly unless cancelled.", {
            locale,
            price,
          }),
          lifetime: I18n.t("%{price} one-time payment. No renewal.", {
            locale,
            price,
          }),
        }[offer.key];
  return {
    title,
    price,
    unit,
    tag,
    detail,
    cta,
    disclosure,
    anchor:
      offer.key === "yearly"
        ? I18n.t("%{price} a year. About what a nanny costs in a day.", {
            locale,
            price,
          })
        : undefined,
    consent:
      offer.key === "lifetime"
        ? I18n.t("By purchasing, you agree to our", { locale })
        : I18n.t("Cancel anytime. By subscribing, you agree to our", {
            locale,
          }),
  };
};
